/**
 * The debug wrapper's default sign-in page notice (owner request 2026-10-02): when the wrapper is
 * active and the operator has set no flag message, the shared `<tb-login-screen>` says which
 * environment this is and where the test users are.
 *
 * - The environment label is `debugWrapper.envMode` from GET /bff/config/login-notice: edge-bff's
 *   EnvModeFilter host map, resolved per request. No hostname parsing here (persona-switch.model.ts
 *   warns against a third host table).
 * - A flag message fully replaces the default; the two never stack.
 * - The drawer sits on the left, except at the debug shell's phone breakpoint, where it stacks
 *   above the login (debug-shell.component.ts `@media (max-width: 640px)`), so the copy follows it.
 */
import { DestroyRef, Signal, inject, signal } from '@angular/core';
import type { LoginNotice } from '@toybank/ui';

/** Mirrors the debug shell's stacked-drawer breakpoint. */
export const DRAWER_STACKED_QUERY = '(max-width: 640px)';
const ENV_MODE = /^[a-z]{1,16}$/;

/** The default notice for `envMode`; null when there is no usable env label. */
export function debugDefaultNotice(envMode: string | null | undefined, narrow: boolean): LoginNotice | null {
  if (!envMode || !ENV_MODE.test(envMode)) return null;
  const where = narrow ? 'the drawer above.' : 'the drawer on the left.';
  return {
    severity: 'info',
    text: `The current environment is ${envMode}, so you can sign in as any test user from ${where}`,
    links: [],
  };
}

/** Precedence: the flag notice wins; otherwise the default, only while the wrapper is active. */
export function composeDebugLoginNotice(
  flagNotice: LoginNotice | null,
  debugWrapper: { envMode: string } | null,
  narrow: boolean,
): LoginNotice | null {
  return flagNotice ?? (debugWrapper ? debugDefaultNotice(debugWrapper.envMode, narrow) : null);
}

/** Tracks DRAWER_STACKED_QUERY. Call in an injection context; the listener ends with it. */
export function drawerStackedSignal(): Signal<boolean> {
  const query = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DRAWER_STACKED_QUERY)
    : null;
  const stacked = signal(query?.matches ?? false);
  if (query && typeof query.addEventListener === 'function') {
    const onChange = (event: MediaQueryListEvent): void => stacked.set(event.matches);
    query.addEventListener('change', onChange);
    inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));
  }
  return stacked.asReadonly();
}
